/**
 * Wave-banner + shape-callout overlays — extracted from GameCanvas.jsx.
 *
 * Two small in-canvas chyron overlays:
 *
 *  - <WaveBanner> :: "// INCOMING · <LABEL>" mid-top banner that fades
 *    in for ~1.8s whenever a new wave/level begins. Coloured by the
 *    wave's tier accent.
 *
 *  - <ShapeCallout> :: top-of-canvas pill showing the level number,
 *    level name, and any tactical shape hints (e.g. "GRID + DIAMOND").
 *    Dismissed permanently when the pilot starts moving by toggling
 *    `shapeCalloutVisible` in the parent.
 *
 * Both are pure JSX driven by props (`banner`, `level`, `endless`,
 * `levelCfg`) — no canvas refs, no hooks. Animation keyframes
 * (`fadeSlideIn`) live in `index.css` and are unchanged.
 */
export function WaveBanner({ banner }) {
  if (!banner) return null;
  return (
    <div
      className="absolute inset-x-0 top-14 sm:top-20 flex justify-center pointer-events-none z-20 fade-in"
      data-testid="wave-banner"
      aria-live="polite"
    >
      <div
        className="border bg-black/75 px-6 py-3 backdrop-blur-sm"
        style={{
          borderColor: banner.color,
          boxShadow: `0 0 28px ${banner.color}66`,
        }}
      >
        <div
          className="font-mono-cosmic text-[9px] tracking-[0.4em] uppercase mb-1 text-center"
          style={{ color: banner.color }}
        >
          // INCOMING
        </div>
        <div
          className="font-heading font-black uppercase text-2xl sm:text-3xl tracking-tighter text-white text-center"
          style={{ textShadow: `0 0 18px ${banner.color}` }}
          data-testid="wave-banner-label"
        >
          {banner.label}
        </div>
      </div>
    </div>
  );
}

export function ShapeCallout({ visible, level, endless, levelCfg }) {
  if (!visible || !levelCfg) return null;
  const num = endless ? (levelCfg.wave || 1) : level;
  return (
    <div
      className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-none max-w-[calc(100%-1rem)]"
      data-testid="shape-callout"
      style={{ animation: "fadeSlideIn 0.6s ease-out" }}
    >
      <div
        className="px-2 sm:px-3 py-1 sm:py-1.5 border border-[#00ffff]/60 bg-black/70 backdrop-blur-sm flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
        style={{ boxShadow: "0 0 16px rgba(0,255,255,0.35)" }}
      >
        <span className="font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.35em] text-[#00ffff] uppercase whitespace-nowrap">
          // {endless ? "WAVE" : "LVL"} {String(num).padStart(3, "0")} ·
        </span>
        <span className="font-heading font-black uppercase text-[10px] sm:text-sm tracking-tight text-white text-center">
          {levelCfg.name}
        </span>
        {levelCfg.shapes && levelCfg.shapes.length > 0 && (
          <span className="font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.2em] text-white/50 uppercase whitespace-nowrap">
            · {levelCfg.shapes.join(" + ")}
          </span>
        )}
      </div>
    </div>
  );
}
